import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { ServiceService } from '../../service.service'; // Add this import

interface Question {
  question_id: string;
  question_text: string;
  question_type: string;
  options: string[];
  is_required: boolean;
  _id: string;
}

interface QuestionnaireSet {
  set_id: string;
  set_name: string;
  questions: Question[];
  _id: string;
}

interface AssignedCustomer {
  customer_id: string;
  customer_name: string;
  assigned_date: string;
  survey_status: string;
  assigned_to_employee: string;
  _id: string;
}

interface Answer {
  question_id: string;
  set_id: string;
  star_rating?: number;
  selected_option?: string;
  text_answer?: string;
  _id: string;
}

interface Response {
  customer_id: string;
  customer_name: string;
  answers: Answer[];
  remarks: string;
  conducted_date: string;
  conducted_by: string;
  _id: string;
}

interface SurveyDetail {
  survey_id: string;
  survey_number: string;
  survey_title: string;
  survey_description: string;
  questionnaire_sets: QuestionnaireSet[];
  assigned_customers: AssignedCustomer[];
  created_by: {
    employee_id: string;
    employee_name: string;
  };
  status: string;
  created_date_time: string;
  responses: Response[];
  updated_at: string;
  total_assigned: number;
  total_completed: number;
  pending_count: number;
}

interface ApiResponse {
  success: boolean;
  message: string;
  data: SurveyDetail;
}

@Component({
  selector: 'app-question-detail',
  templateUrl: './question-detail.component.html',
  styleUrls: ['./question-detail.component.css']
})
export class QuestionDetailComponent implements OnInit {
  surveyDetail: SurveyDetail | null = null;
  isLoading: boolean = false;
  surveyId: string = '';
  activeTab: string = 'overview'; // overview, questions, assignments, responses

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private service: ServiceService // Add this
  ) { }

  ngOnInit(): void {
    // Get survey ID from route parameter
    this.route.params.subscribe(params => {
      this.surveyId = params['id'];
      if (this.surveyId) {
        this.loadSurveyDetail();
      }
    });
  }

  loadSurveyDetail(): void {
    this.isLoading = true;
    
    this.service.getCustomerSurveyDetail(this.surveyId).subscribe({
      next: (response: ApiResponse) => {
        if (response.success) {
          this.surveyDetail = response.data;
        } else {
          this.service.showToast('error', response.message || 'Failed to load survey details');
        }
        this.isLoading = false;
      },
      error: (error: any) => {
        console.error('Error loading survey detail:', error);
        this.service.showToast('error', 'Failed to load survey details');
        this.isLoading = false;
      }
    });
  }

  setActiveTab(tab: string): void {
    this.activeTab = tab;
  }

  goBack(): void {
    this.router.navigate(['/admin/customer-survey-menu/listing']);
  }

  onEdit(): void {
    this.router.navigate(['/admin/customer-survey-menu/edit', this.surveyId]);
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Completed':
      case 'Conducted':
        return 'status-completed';
      case 'In Progress':
        return 'status-in-progress';
      case 'Pending':
        return 'status-pending';
      default:
        return '';
    }
  }

  getCompletionPercentage(): number {
    if (!this.surveyDetail || this.surveyDetail.total_assigned === 0) {
      return 0;
    }
    return Math.round((this.surveyDetail.total_completed / this.surveyDetail.total_assigned) * 100);
  }

  getQuestionText(questionId: string): string {
    if (!this.surveyDetail) return '';
    
    for (const set of this.surveyDetail.questionnaire_sets) {
      const question = set.questions.find(q => q.question_id === questionId);
      if (question) {
        return question.question_text;
      }
    }
    return questionId;
  }

  getStarArray(rating: number): number[] {
    return Array(5).fill(0).map((_, i) => i < rating ? 1 : 0);
  }

  getTotalQuestions(): number {
    if (!this.surveyDetail) return 0;
    return this.surveyDetail.questionnaire_sets.reduce((sum, set) => sum + set.questions.length, 0);
  }
}